import { Col, Form, Select } from 'antd'
import React, { ReactNode, useMemo, useRef, useState } from 'react'

import { FormSortableSelectTag } from './FormSortableSelectTag'

interface Option {
  label: string
  value: string
}

interface Props {
  span?: string | number
  label?: ReactNode
  placeholder?: string
  value?: string[]
  onChange: (value: string[]) => void
  options: Option[]
  required?: boolean
  disabled?: boolean
  tooltip?: string

  colProps?: React.ComponentProps<typeof Col>
  itemProps?: React.ComponentProps<typeof Form.Item>
  selectProps?: React.ComponentProps<typeof Select>
  xs?: number
  sm?: number
  md?: number
  lg?: number
  xl?: number
  xxl?: number

  getTagId?: (value: string) => string
}

let uniq = 0
const getUniqueId = () => `${Date.now()}_${uniq++}`

export const FormSortableSelect: React.FC<Props> = ({
  placeholder,
  value,
  onChange,
  options,
  disabled,
  selectProps,
  getTagId,
}) => {
  const containerIdRef = useRef(`selectDragContainer_${getUniqueId()}`)

  const dndScope = useMemo(() => `dndScope_${getUniqueId()}`, [])

  const getContainer = () => document.querySelector(`#${containerIdRef.current}`) as HTMLElement | null

  const getWrappers = (withDragged?: 'withDragged') => {
    const container = getContainer()
    if (!container) return []

    const wrappers = Array.from(
      container.querySelectorAll(`span[data-sort-id][data-dnd-scope="${dndScope}"]`),
    ) as HTMLElement[]

    if (withDragged) return wrappers

    return wrappers.filter((w) => !w.querySelector(`.draggable-tag[data-dnd-scope="${dndScope}"][data-dragging="1"]`))
  }

  const getDraggedWrapper = () => {
    const container = getContainer()
    if (!container) return null

    const draggedTag = container.querySelector(`.draggable-tag[data-dnd-scope="${dndScope}"][data-dragging="1"]`)
    if (!draggedTag) return null

    return (draggedTag.closest(`span[data-sort-id][data-dnd-scope="${dndScope}"]`) as HTMLElement) || null
  }

  const getElementOnRight = (cursorX: number, cursorY: number) => {
    const wrappers = getWrappers()

    return wrappers.reduce(
      (closest, element, index) => {
        const box = element.getBoundingClientRect()
        const nextBox = wrappers[index + 1]?.getBoundingClientRect()

        const inSameRowAsCursor = cursorY - (box.bottom + 4) <= 0 && cursorY - box.top >= 0
        const offset = cursorX - (box.left + box.width / 2)

        if (!inSameRowAsCursor) return closest

        if (offset < 0 && offset > closest.offset) {
          return { offset, element }
        }

        if (nextBox && cursorY - nextBox.top <= 0 && closest.offset === Number.NEGATIVE_INFINITY) {
          return { offset: 0, element: wrappers[index + 1] }
        }

        return closest
      },
      { offset: Number.NEGATIVE_INFINITY } as { offset: number; element?: HTMLElement },
    ).element
  }

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()

    const container = getContainer()
    if (!container) return

    const draggedWrapper = getDraggedWrapper()
    if (!draggedWrapper) return

    const elementOnRight = getElementOnRight(e.clientX, e.clientY)

    if (elementOnRight) {
      elementOnRight.insertAdjacentElement('beforebegin', draggedWrapper)
    } else {
      const wrappers = getWrappers()
      if (wrappers.length === 0) return
      wrappers[wrappers.length - 1].insertAdjacentElement('afterend', draggedWrapper)
    }
  }

  const [key, setKey] = useState(getUniqueId())

  const onDragEnd = () => {
    const wrappers = getWrappers('withDragged')

    const newValues = wrappers.map((w) => w.dataset.sortId).filter(Boolean) as string[]

    if (newValues.length > 0) {
      onChange(newValues)
      setKey(getUniqueId())
      return
    }

    // Fallback
    const labels = wrappers.map((w) => w.textContent || '')
    const mapped = labels.map((l) => options.find((option) => option.label === l)?.value).filter(Boolean) as string[]

    onChange(mapped)
    setKey(getUniqueId())
  }

  const tagRender = (tagProps: any) => {
    const { value: tagValue } = tagProps
    const tagId = getTagId ? getTagId(String(tagValue)) : String(tagValue)

    return (
      <span data-sort-id={tagId} data-dnd-scope={dndScope} style={{ display: 'inline-flex' }}>
        <FormSortableSelectTag dndScope={dndScope} onDragEnd={onDragEnd} {...tagProps} />
      </span>
    )
  }

  return (
    <div id={containerIdRef.current} onDragOver={onDragOver}>
      <Select
        key={key}
        value={value}
        onChange={(e) => onChange(e as string[])}
        placeholder={placeholder}
        disabled={disabled}
        showSearch
        allowClear
        mode="multiple"
        className={`sortable-multi-select ${selectProps?.className ?? ''}`.trim()}
        optionFilterProp="label"
        filterOption={(input, option: any) =>
          String(option?.label ?? '')
            .toLowerCase()
            .includes(input.toLowerCase())
        }
        tagRender={tagRender}
        options={options}
        {...selectProps}
      />
    </div>
  )
}
